// Temporary fix to handle widget filtering issues when sharing filters with the datatable.
// When a date filter is set by period, it may change the filter state to [undefined, undefined],
// which causes errors in the backend. As a patch, this fix prevents the widget from breaking
// and disappearing until a permanent solution is implemented on the datatable.
import {
  BankAccountsTransactionsWidgetQuery,
  DateFilter,
  DateTimeFilter,
  InputMaybe,
} from 'generated/resources/graphql'
import moment from 'moment'

import { ChartData } from './types'

type Filter = DateFilter | DateTimeFilter | unknown

export const removeNullBetween = <T extends Record<string, InputMaybe<Filter>>>(filters: T): T => {
  const result: Partial<T> = {}

  for (const key in filters) {
    const filter = filters[key]

    if (!hasInvalidBetween(filter)) {
      result[key] = filter
    }
  }

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return result as T
}

// This function checks if the filter has a between property with at least one undefined value
const hasInvalidBetween = (filter: unknown): filter is { between: (unknown | undefined)[] } => {
  return (
    typeof filter === 'object' &&
    filter !== null &&
    'between' in filter &&
    Array.isArray(filter.between) &&
    filter.between.some((val) => val === undefined)
  )
}

export type MonthsIntervalChartDataMap = {
  [key: string]: { date: string; label: string; value: { [key: string]: number } }
}
export const generateMonthsIntervalForChartDataMap = (
  minDate: Date,
  maxDate: Date,
  valueKeys: string[]
): MonthsIntervalChartDataMap => {
  const chartDataMap: MonthsIntervalChartDataMap = {}

  const monthsDifference =
    maxDate.getFullYear() * 12 +
    maxDate.getMonth() -
    (minDate.getFullYear() * 12 + minDate.getMonth())

  if (monthsDifference + 1 < 12) {
    const extraMonths = 12 - (monthsDifference + 1)
    minDate = new Date(minDate)
    minDate.setMonth(minDate.getMonth() - extraMonths)
  }

  const currentDate = new Date(minDate)

  while (moment(currentDate).isSameOrBefore(maxDate, 'month')) {
    const month = moment(currentDate).format('MMM')
    const year = moment(currentDate).format('YY')
    const monthAndYear = `${month} ${year}`

    if (!chartDataMap[monthAndYear]) {
      const value: { [key: string]: number } = {}
      valueKeys.forEach((key) => {
        value[key] = 0
      })
      chartDataMap[monthAndYear] = {
        date: moment(currentDate).format('YYYY-MM-DD'),
        label: monthAndYear,
        value,
      }
    }

    currentDate.setMonth(currentDate.getMonth() + 1)
  }

  return chartDataMap
}

export const generateMonthlyChartData = (data: BankAccountsTransactionsWidgetQuery) => {
  const incomeValues =
    data?.banking?.incomeTransactions?.aggregate?.terms?.bookingDate?.values || []
  const incomeAmounts =
    data?.banking?.incomeTransactions?.aggregate?.terms?.bookingDate?.sum?.amountCents || []

  const outcomeValues =
    data?.banking?.outcomeTransactions?.aggregate?.terms?.bookingDate?.values || []
  const outcomeAmounts =
    data?.banking?.outcomeTransactions?.aggregate?.terms?.bookingDate?.sum?.amountCents || []

  let chartDataMap: MonthsIntervalChartDataMap = {}
  const allDates = [...incomeValues, ...outcomeValues].map((date) => new Date(date))
  const hasAnyData = allDates.length > 0

  if (hasAnyData) {
    const minDate = new Date(Math.min(...allDates.map((date) => date.getTime())))
    const maxDate = new Date(Math.max(...allDates.map((date) => date.getTime())))
    const valueKeys = ['income', 'outcome']

    chartDataMap = generateMonthsIntervalForChartDataMap(minDate, maxDate, valueKeys)

    incomeValues.forEach((date, index) => {
      const month = moment(date).format('MMM YY')
      if (chartDataMap[month] && chartDataMap[month].value.hasOwnProperty('income')) {
        chartDataMap[month].value.income += incomeAmounts[index] || 0
      }
    })

    outcomeValues.forEach((date, index) => {
      const month = moment(date).format('MMM YY')
      if (chartDataMap[month] && chartDataMap[month].value.hasOwnProperty('outcome')) {
        chartDataMap[month].value.outcome += outcomeAmounts[index] || 0
      }
    })
  }

  return Object.values(chartDataMap)
}

export const cleanNotFilteredMonths = (
  chartDatas: ChartData[],
  factoryZeroFunction: (
    date: moment.Moment,
    key: string
  ) => { date?: string; label: string; value: { [key: string]: number } },
  timeFilter?: DateTimeFilter | null
): ChartData[] => {
  const startDate = moment(timeFilter?.between?.[0], moment.ISO_8601, true)
  const endDate = moment(timeFilter?.between?.[1], moment.ISO_8601, true)

  if (!startDate.isValid() || !endDate.isValid()) {
    return chartDatas
  }

  const dataMap: { [key: string]: ChartData } = {}
  chartDatas.forEach((entry) => {
    const date = moment(entry.label, ['MMM YY', 'MMM. YY'], true)
    if (date.isValid()) {
      const key = date.format('MMM YY')
      dataMap[key] = entry
    }
  })

  const result: ChartData[] = []
  const current = startDate.clone().startOf('month')
  const lastMonth = endDate.clone().startOf('month')

  while (current.isSameOrBefore(lastMonth)) {
    const key = current.format('MMM YY')
    const dataMapValue = dataMap[key]
    if (dataMapValue) {
      result.push({
        ...dataMapValue,
        value: {
          ...factoryZeroFunction(current, key).value,
          ...(typeof dataMapValue.value === 'number'
            ? { value: dataMapValue.value }
            : dataMapValue.value),
        },
      })
    } else {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      result.push(factoryZeroFunction(current, key) as unknown as ChartData)
    }
    current.add(1, 'month')
  }

  return result
}
